// @flow

import { createContext, useCallback, useContext, useEffect } from 'react';

export const PublicEventBusContext = createContext<?PublicEventBus>(null);

export type EventListener = (...args: any[]) => void | Promise<void>;

export class PublicEventBus {
  listeners: { [string]: EventListener[] } = {};
  cache: { [string]: any } = {};

  on(name: string, listener: EventListener) {
    const listeners = (this.listeners[name] = this.listeners[name] || []);

    listeners.push(listener);

    if (this.cache[name]) {
      listener(...this.cache[name]);
      delete this.cache[name];
    }
  }

  off(name: string, listener: EventListener) {
    this.listeners[name] = (this.listeners[name] || []).filter(l => l !== listener);
  }

  trigger(name: string, ...params: any[]) {
    const listeners = (this.listeners[name] = this.listeners[name] || []);

    if (!listeners.length) {
      this.cache[name] = params;
      return;
    }

    listeners.forEach(listener => listener(...params));
  }
}

export function useEventBusListener(name: string, callback: EventListener, dependencies?: any[]) {
  const bus = useEventBusSafely();

  const listener = useCallback((...args) => {
    (async () => {
      await callback(...args);
    })();
  }, dependencies);

  useEffect(() => {
    bus.on(name, listener);
    return () => bus.off(name, listener);
  }, dependencies);
}

export function useEventBusPublisher(name: string, ...params: any[]) {
  const bus = useEventBusSafely();

  useEffect(() => {
    bus.trigger(name, ...params);
  }, [name, ...params]);
}

export function useEventBusSafely(): PublicEventBus {
  const bus = useContext(PublicEventBusContext);

  if (!bus) {
    throw new Error('useEventBusSafely - missing EventBus');
  }

  return bus;
}
